import React, { useDeferredValue, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useStaticQuery, graphql, Link } from 'gatsby';
import kebabCase from 'lodash/kebabCase';
import sortBy from 'lodash/sortBy';
import { createURL } from 'utils/createURL';
import { LUCIDWORKS_PLATFORM_TAG, isValidProductVersion, productItems } from 'config/productItems';
import { hideAppTypes } from 'config/appTypes';
import SidenavFilter from 'components/Sidebar/SidenavFilter/SidenavFilter.component';
import NavItem from '../NavItem/NavItem.component'
import { active } from './product-nav.module.scss'


// Group Fusion 4 products together in sidenav under Fusion 4 option in Product/Version selector
const fusion4 = productItems.filter(item => item.subMenu === 'Fusion 4')

// helper function to ensure proper version is set for link in fusion4Nav in below component
function cleanLink(link){
  if(link.charAt(0) !== '/') return link
  return link.slice(1)
}

function ProductNav({ 
  basePath, 
  recentVersion, 
  productInfo, 
  currentLink, 
  currentProduct 
}){
  const data = useStaticQuery(graphql`
    query {
      allAsciidoc(sort: { frontmatter: { title: ASC } }) {
        edges {
          node {
            frontmatter {
              title
              skipBefore
              skipAfter
              permalink
              order
              skip
              topindex
              subGroup
            }
            paths {
              from {
                source {
                  file
                }
              }
            }
          }
        }
      }
    }
  `);

  const isFusion4 = parseFloat(recentVersion) <= 5 && productInfo.subMenu === 'Fusion 4'
  const isLucidworksPlatform = productInfo.tag === LUCIDWORKS_PLATFORM_TAG
  
  const navItems1 = []
  const navItems2 = []
  const navItems3 = []

  /**
  * Checks if a version/app-type is valid for the page.
  *
  * @param {object} node The asciidoc data of a page 
  * @param {string} version the version or app type of the page
  * @param {string} pathVersion the version extracted from the node source path
  * @return {bool} if version is valid for node
  */
  function isValidVersion(node, version, pathVersion) {
    if (
      productInfo &&
      productInfo.versions &&
      !node.paths.from.source.file.includes(`/${version}/`) ||
      pathVersion === node?.frontmatter?.skip ||
      pathVersion !== version
    )
      return false;
    return isValidProductVersion(node.frontmatter, version);
  };

  function addSubItem(subNavItems, node, splitPath, path, index){
    const nodeTitle = node?.frontmatter?.title ?? 'No Title';
    const nodeUUID = node?.frontmatter?.permalink ??  '';
    const nodeOrder = node?.frontmatter?.order ?? 9999

    if (splitPath[index] === 'index') {
      const rootItem = subNavItems.find(item => item.path === `/${path}`);
      if (rootItem) {
        rootItem.name = nodeTitle;
        rootItem.link = createURL(splitPath, nodeUUID, productInfo) + `/${kebabCase(nodeTitle)}`; /* eslint-disable-line prefer-template */
        rootItem.order = nodeOrder;
      } else {
        subNavItems.push({
          name: nodeTitle,
          path: `/${path}`,
          link: createURL(splitPath, nodeUUID, productInfo) + `/${kebabCase(nodeTitle)}`,  /* eslint-disable-line prefer-template */
          order: nodeOrder,
          subItems: [],
        });
      }
    } else if (index === splitPath.length) {
      if (node.frontmatter.topindex) return;
      subNavItems.push({
        name: nodeTitle,
        path: `/${path}`,
        link: createURL(splitPath, nodeUUID, productInfo) + `/${kebabCase(nodeTitle)}`,  /* eslint-disable-line prefer-template */
        order: nodeOrder,
      });
    } else {


      const pathSegment = splitPath.slice(0, index).join('/');
      const indexPath = `/${pathSegment}/index`;
      const rootItem = subNavItems.find(item => item.path === indexPath);
      if (!rootItem) {
        subNavItems.push({ name: 'unknown', path: indexPath, subItems: [], active: false });
      }

      addSubItem(
        subNavItems.find(item => item.path === indexPath).subItems,
        node,
        splitPath,
        path,
        index + 1,
        subNavItems.find(item => item.path === indexPath).link,
      );
    }
  };

  function createFusion4Nav(node, path, splitPath, version){
    if (isValidVersion(node, version, splitPath[1])) {
      const index = productInfo.versions ? 3 : 2;
      if (splitPath.length >= index && node.frontmatter.skip !== 'all') {
        if(splitPath[0] === "fusion-server"){
          addSubItem(navItems1, node, splitPath, path, index);
        }
        if(splitPath[0] === 'fusion-ai'){
          addSubItem(navItems2, node, splitPath, path, index);
        }
        if(splitPath[0] === 'app-studio'){
          addSubItem(navItems3, node, splitPath, path, index);
        }
      }
    }
  }

  function createSubGroupNav(node, path, splitPath, navList, version){
    if (splitPath[0] === basePath) {
      const index = productInfo.versions || productInfo.apptypes ? 3 : 2;
      if (splitPath.length >= index && node.frontmatter.skip !== 'all') {
        if(splitPath.length <= 3 && (version !== splitPath[1])){
          return;
        }
        if(hideAppTypes.includes(node?.frontmatter?.subGroup)){
          return
        }
        addSubItem(navList, node, splitPath, path, index)
      }
    }
  }

  function createProductNav(node, path, splitPath, navList, version){
      // const validAttributes = isValidVersion(node, version, split)

      const hasAttributes = productInfo.versions || productInfo.apptypes
      if (splitPath[0] === basePath && (isValidVersion(node, version, splitPath[1]) || !hasAttributes)) {
        const index = productInfo.versions || productInfo.apptypes ? 3 : 2;
        if (splitPath?.length >= index && node?.frontmatter?.skip !== 'all') {
          addSubItem(navList, node, splitPath, path, index)
        }
      }
  }

  function getNavItems(queryData, version){
    let navList = []

    for(let i = 0; i < queryData.allAsciidoc.edges.length; i++){
      const { node } = queryData.allAsciidoc.edges[i]
      const path = node.paths.from.source.file;
      const splitPath = path.split('/');
      if(isFusion4){
        createFusion4Nav(node, path, splitPath, version)
      } else if (isLucidworksPlatform){
        createSubGroupNav(node, path, splitPath, navList, version)
      } else {
        createProductNav(node, path, splitPath, navList, version)
      }
    }

    if(isFusion4 || basePath === '/'){
      const fusion4Nav = [navItems1, navItems2, navItems3]
      navList = fusion4.map((item, indx) => ({
        name: item.name,
        link: cleanLink(item.link.replace('$version', version)),
        path: item.alternateLink,
        tag: item.tag,
        order: indx + 1,
        subItems: [...fusion4Nav[indx]]
      }))
    }
    return navList
  }

  const flattened = (obj, myArr) => {
    if(!myArr){
      myArr = []
    }

    for(let j = 0; j < obj.length; j++){
      const item = obj[j]
      myArr.push({
        link: item.link,
        name: item.name,
      })
      if(item.subItems){
        flattened(item.subItems, myArr)
      }
    }
  
    return myArr
  }

  // parse docs to create nested list of sidebar nav items
  const navItems = useMemo(() => {
    return getNavItems(data, recentVersion)
  }, [data, recentVersion, productInfo]) // eslint-disable-line react-hooks/exhaustive-deps

  // consume parsed docs in sidenav to give valid list for filter navigation
  const deferredNavItems = useDeferredValue(navItems)
  const flattenedNav = useMemo(() => {
    return flattened(navItems)
  }, [deferredNavItems]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <SidenavFilter flattenedItems={flattenedNav} version={recentVersion} />

      <Link to='/' activeClassName={active}>Home</Link>
      <Link to="/how-to" activeClassName={active}>How-to Guides</Link>

      <Link to={currentLink} style={{textTransform: 'capitalize'}} activeClassName="active">{isLucidworksPlatform ? "Lucidworks Platform" : currentProduct}</Link>
      {navItems.length > 0 ? <ul className="topic-selector">
        {sortBy(navItems, 'order').map(navItem => (
          <NavItem key={navItem.name} navItem={navItem} />
        ))}
      </ul> : null}
    </>
  );
};

ProductNav.propTypes = {
  recentVersion: PropTypes.string.isRequired,
  basePath: PropTypes.string.isRequired,
  productInfo: PropTypes.object.isRequired,
  currentLink: PropTypes.string.isRequired,
  currentProduct: PropTypes.string.isRequired, 
};

export default ProductNav;
